import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { Stethoscope, Users, Briefcase, Calendar, Plane, ArrowRight, CheckCircle2, GraduationCap } from 'lucide-react';
export function Services() {
  const services = [{
    id: 'medical',
    title: 'Handicap Accessible Transportation',
    icon: <Stethoscope className="w-8 h-8" data-id="element-1070" />,
    description: 'We provide safe and comfortable transportation for individuals with mobility challenges. Our accessible vehicles are designed to accommodate wheelchairs and mobility devices while ensuring a smooth and secure ride.',
    audience: 'Individuals with mobility challenges needing safe and comfortable transport.',
    vehicle: 'Wheelchair Accessible Vans',
    assistance: 'Full Door-Through-Door Assistance',
    features: ['Medical appointment transportation', 'Assisted mobility transport', 'Wheelchair accessible rides', 'Transportation for seniors and individuals with disabilities', 'Personal transportation for daily needs'],
    bgImage: 'https://cdn.magicpatterns.com/uploads/8yCBxQ4VekKJ9njQkXWFAY/pexels-mingao-welfarecar-555708794-24415252_(1).jpg'
  }, {
    id: 'event',
    title: 'Local & Long Distance Transportation',
    icon: <Calendar className="w-8 h-8" data-id="element-1071" />,
    description: 'Whether you need a short trip across town or long-distance transportation, we offer dependable services designed to get you where you need to go safely and comfortably.',
    audience: 'Individuals and groups needing dependable travel arrangements.',
    vehicle: 'Comfortable Sedans & Spacious Vans',
    assistance: 'Customized to passenger needs',
    features: ['Private transportation services', 'Airport transportation', 'Event transportation', 'Long-distance travel arrangements'],
    bgImage: 'https://cdn.magicpatterns.com/uploads/8XoRQq2QitMaiztuXGRstW/william-william-NndKt2kF1L4-unsplash.jpg'
  }, {
    id: 'school',
    title: 'School Runs for Kids',
    icon: <GraduationCap className="w-8 h-8" data-id="element-1072" />,
    description: 'Safe, reliable, and affordable school transportation for your children. We handle daily pick-ups and drop-offs so parents can have peace of mind.',
    audience: 'Parents and guardians needing dependable school transportation for their children.',
    vehicle: 'Comfortable & Safe Vehicles',
    assistance: 'Supervised pick-up and drop-off',
    features: ['Daily school pick-up and drop-off', 'Consistent and punctual scheduling', 'Safe and supervised rides', 'Affordable daily and weekly rates', 'After-school activity transportation'],
    bgImage: 'https://cdn.magicpatterns.com/uploads/qPtUvWRLWf5uHr3tPG6tqd/pexels-yankrukov-8617515.jpg'
  }, {
    id: 'shipping',
    title: 'Shipping & Delivery Services',
    icon: <Briefcase className="w-8 h-8" data-id="element-1073" />,
    description: 'We provide affordable and reliable shipping services for packages, equipment, and important items.',
    audience: 'Individuals and businesses needing secure transport of goods.',
    vehicle: 'Large Capacity Cargo Vans',
    assistance: 'Full loading and unloading included',
    features: ['Local package delivery', 'Business shipping support', 'Secure transport of goods', 'Scheduled delivery services'],
    bgImage: 'https://cdn.magicpatterns.com/uploads/jyJu5fXQtoWWEFZy5Fh2QL/pexels-andromeda99-11935038.jpg'
  }];
  return <div className="bg-warm-50 min-h-screen pb-20" data-id="element-1074">
      {/* Header */}
      <div className="bg-primary-900 text-white py-16" data-id="element-1075">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1076">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-1077">Our Services</h1>
          <p className="text-xl text-primary-100 max-w-3xl mx-auto" data-id="element-1078">
            Comprehensive transportation and shipping solutions designed to move
            people and packages with care, reliability, and affordability.
          </p>
        </div>
      </div>

      {/* Services List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8" data-id="element-1079">
        <div className="space-y-8" data-id="element-1080">
          {services.map((service, index) => <Card key={service.id} className="overflow-hidden shadow-lg border-none" data-id="element-1081">
              <div className="flex flex-col md:flex-row" data-id="element-1082">
                <div className="md:w-1/3 p-8 flex flex-col justify-center text-white relative overflow-hidden min-h-[280px]" data-id="element-1083">
                  <img src={service.bgImage} alt="" className="absolute inset-0 w-full h-full object-cover" data-id="element-1084" />
                  <div className="absolute inset-0 bg-black/65" data-id="element-1085" />
                  <div className="relative z-10" data-id="element-1086">
                    <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mb-6 backdrop-blur-sm" data-id="element-1087">
                      {service.icon}
                    </div>
                    <h2 className="text-2xl font-bold mb-4" data-id="element-1088">{service.title}</h2>
                    <p className="text-white/90 text-lg leading-relaxed" data-id="element-1089">
                      {service.description}
                    </p>
                  </div>
                </div>
                <div className="md:w-2/3 p-8 bg-white" data-id="element-1090">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8" data-id="element-1091">
                    <div data-id="element-1092">
                      <h3 className="text-sm font-bold text-warm-400 uppercase tracking-wider mb-2" data-id="element-1093">
                        Who It's For
                      </h3>
                      <p className="text-warm-800 font-medium" data-id="element-1094">
                        {service.audience}
                      </p>
                    </div>
                    <div data-id="element-1095">
                      <h3 className="text-sm font-bold text-warm-400 uppercase tracking-wider mb-2" data-id="element-1096">
                        Vehicles Used
                      </h3>
                      <p className="text-warm-800 font-medium" data-id="element-1097">
                        {service.vehicle}
                      </p>
                    </div>
                    <div data-id="element-1098">
                      <h3 className="text-sm font-bold text-warm-400 uppercase tracking-wider mb-2" data-id="element-1099">
                        Assistance Level
                      </h3>
                      <p className="text-warm-800 font-medium" data-id="element-1100">
                        {service.assistance}
                      </p>
                    </div>
                  </div>

                  <div className="mb-8" data-id="element-1101">
                    <h3 className="text-sm font-bold text-warm-400 uppercase tracking-wider mb-4" data-id="element-1102">
                      Key Features
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-1103">
                      {service.features.map((feature, i) => <li key={i} className="flex items-start" data-id="element-1104">
                          <CheckCircle2 className="w-5 h-5 text-secondary-500 mr-2 flex-shrink-0" data-id="element-1105" />
                          <span className="text-warm-700" data-id="element-1106">{feature}</span>
                        </li>)}
                    </ul>
                  </div>

                  <Link to={`/booking?service=${service.id}`} data-id="element-1107">
                    <Button variant={index % 2 === 0 ? 'primary' : 'secondary'} data-id="element-1108">
                      Book This Service <ArrowRight className="ml-2 w-4 h-4" data-id="element-1109" />
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>)}
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-4xl mx-auto mt-20 px-4 text-center" data-id="element-1110">
        <h2 className="text-2xl font-bold text-warm-900 mb-4" data-id="element-1111">
          Don't see exactly what you need?
        </h2>
        <p className="text-warm-600 mb-8" data-id="element-1112">
          We often accommodate special requests and unique transportation needs.
          Contact our team to discuss how we can help.
        </p>
        <Link to="/contact" data-id="element-1113">
          <Button variant="outline" size="lg" className="bg-white" data-id="element-1114">
            Contact Us to Discuss Custom Needs
          </Button>
        </Link>
      </div>
    </div>;
}